import React, {useEffect} from 'react'
import clsx from 'clsx'
import {useOutletContext, useRouteLoaderData} from '@remix-run/react'
import type {FunctionComponent, SyntheticEvent} from 'react'
import {getTranslation} from '~/localization/translation'
import {IconChevronRightThick} from '~/icons/brg-icon-chevron-right-thick'
import {useReportWidgetClick, useReportWidgetSeen, useSendPartnerProductClickoutCustom} from '~/hooks/analytic-hooks'
import {KeyFeaturesIcon} from '~/icons/product-page/key-features-icon'
import type {Product, RootAndListContext, RootLoaderResponse} from '~/types'
import {getDomainConfig} from '~/domains'

interface KeyFeaturesSpecsLayoutProps {
  placement: number
  productPageSlug?: string
  showProductPageLinks?: boolean
  affiliateLinkWithClickId: string
  product: Product
  isKeyFeaturesOpen: boolean
  setIsKeyFeaturesOpen: (value: boolean) => void
  handleCardClick: (e: SyntheticEvent) => void
}
const KeyFeaturesSpecsLayout: FunctionComponent<KeyFeaturesSpecsLayoutProps> = ({
  placement,
  isKeyFeaturesOpen,
  setIsKeyFeaturesOpen,
  productPageSlug,
  showProductPageLinks = true,
  product,
  affiliateLinkWithClickId,
}) => {
  const {slug} = useOutletContext<RootAndListContext>()
  const {domain, locale, featureFlags, language, forcedLanguage} = useRouteLoaderData('root') as RootLoaderResponse
  const [productUrl, setProductUrl] = React.useState<string | undefined>(undefined)
  const translations = getTranslation(language)
  const reportClick = useReportWidgetClick()
  const reportClickout = useSendPartnerProductClickoutCustom()
  const {styles: stylesBydomain} = getDomainConfig(domain, locale)

  const showFullReviewButton = showProductPageLinks && featureFlags?.useProductFeaturesPage && productPageSlug

  const [widgetRef] = useReportWidgetSeen({
    widget_name: 'engaging-key-features',
    widget_place: `card-${placement}`,
  })
  const [readFullReviewRef] = useReportWidgetSeen({
    widget_name: 'read-full-review-cta',
    widget_place: `card-${placement}`,
  })

  function handleSeeFullReviewClick() {
    reportClick({
      widget_name: 'read-full-review-cta',
      widget_place: `card-${placement}`,
    })
    reportClickout({
      index: placement,
      item_list_id: slug || '',
      click_element_name: 'read-full-review-cta',
      partner_item_brand: product?.brand || '',
      partner_item_id: product?.asin || '',
      item_name: product?.name || '',
      item_brand: product?.brand || '',
      click_id: product?.affiliateLink?.clickId,
      affiliation: product?.affiliateLink?.type,
      is_dws: product?.isSellerProduct,
      location_id: locale,
      traffic: 'unknown',
      gclid: undefined,
      sys_id: undefined,
      target: affiliateLinkWithClickId,
    })
    setTimeout(() => window.open(affiliateLinkWithClickId, '_self', 'noopener,noreferrer'), 1000)
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname
      const newPath = `${currentPath}/${productPageSlug}`
      const newTabUrl = `${window.location.origin}${newPath}`
      setProductUrl(newTabUrl)
    }
  }, [productPageSlug])

  const showKeyFeatures =
    product?.keyFeatures && product?.keyFeatures?.length > 0 && forcedLanguage?.toLowerCase() !== 'es'

  return showKeyFeatures ? (
    <div ref={widgetRef} className="tablet:bg-[#F1F5F9]">
      <div
        className={clsx(
          'category-card-content max-h-fit overflow-hidden border-0 border-t border-solid transition-all duration-300 ease-in-out tablet:border-none',
          isKeyFeaturesOpen ? 'h-[500px]' : 'h-0',
        )}
      >
        <div className="mb-1 mt-5 flex flex-row items-center gap-2">
          <KeyFeaturesIcon />
          <p className="text-base font-semibold">{translations.ProductCard.keyFeatures}</p>
        </div>
        <ul className="list-outside px-1" data-testid="key-feature-content">
          {product?.keyFeatures?.slice(0, 5)?.map(({feature, description}, index) => (
            <li key={`${feature}-${index}`} className="mb-1 flex flex-row items-start">
              <p className="mb-auto text-sm font-medium">{index + 1}.</p>
              <p
                className={clsx(
                  'ml-1 text-sm font-light [&>b]:font-semibold',
                  !isKeyFeaturesOpen && 'line-clamp-1 pr-44 tablet:line-clamp-2 tablet:pr-0',
                )}
              >
                <b>{feature}</b>: {description}
              </p>
            </li>
          ))}
        </ul>
      </div>

      <div
        className="category-card-content my-3 flex flex-row justify-between tablet:bg-[#F1F5F9]"
        ref={readFullReviewRef}
      >
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setIsKeyFeaturesOpen(!isKeyFeaturesOpen)
            reportClick({
              widget_name: 'expand-key-features-button',
              widget_place: `card-${placement}`,
              trigger_type: isKeyFeaturesOpen ? 'close' : 'open',
            })
          }}
          className={clsx(
            'flex h-fit flex-row items-center gap-2 rounded-2xl border px-4 py-1.5 text-sm',
            stylesBydomain.LISTPAGE_BUTTON_NEW_LAYOUT_SECONDARY,
            'border',
            'listpage_button_new_layout_secondary',
          )}
        >
          <p>{translations.ProductCard.keyFeatures}</p>
          <IconChevronRightThick
            className={clsx(
              'size-3 transition-all duration-200 ease-in-out',
              isKeyFeaturesOpen ? '-rotate-90' : 'rotate-90',
              'tablet:hidden',
              'tablet:size-3',
              stylesBydomain.LISTPAGE_BUTTON_NEW_LAYOUT_ICON_FILL,
              'listpage_button_new_layout_icon_fill',
            )}
          />
        </button>
        {showFullReviewButton ? (
          <a
            className={clsx(
              'flex flex-row items-center gap-2 rounded-2xl border px-4 py-1.5 text-sm',
              stylesBydomain.LISTPAGE_BUTTON_NEW_LAYOUT_SECONDARY,
              'listpage_button_new_layout_secondary',
            )}
            href={productUrl}
            target="_blank"
            aria-label={translations.ProductCard.viewFullReview}
            rel="noreferrer"
            onClick={handleSeeFullReviewClick}
          >
            <span>{translations.ProductCard.viewFullReview}</span>
          </a>
        ) : null}
      </div>
    </div>
  ) : null
}

export default KeyFeaturesSpecsLayout
